import { useEffect, useRef, useState } from 'react';
import type { User } from 'firebase/auth';

import { signOutUser } from '../../services/firebase/auth';

interface PendingApprovalProps {
  authUser: User;
  onRefreshApproval: () => Promise<void>;
}

const APPROVAL_POLL_INTERVAL_MS = 15_000;

export function PendingApproval({
  authUser,
  onRefreshApproval,
}: PendingApprovalProps): JSX.Element {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const onRefreshApprovalRef = useRef(onRefreshApproval);
  onRefreshApprovalRef.current = onRefreshApproval;

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void onRefreshApprovalRef.current().catch(() => undefined);
    }, APPROVAL_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const handleRefresh = async (): Promise<void> => {
    setIsRefreshing(true);
    setStatusMessage(null);

    try {
      await onRefreshApproval();
      setStatusMessage('Still pending approval. Check back in a minute.');
    } catch (error) {
      console.warn('[auth] Approval refresh failed', error);
      setStatusMessage('Could not refresh approval status right now.');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex h-full w-full items-center justify-center bg-slate-950 px-6 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900/80 p-8 shadow-2xl">
        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.22em] text-amber-300">
          Pending Approval
        </p>
        <h1 className="mb-3 text-3xl font-semibold text-slate-100">Thanks for registering.</h1>
        <p className="mb-4 text-sm leading-6 text-slate-300">
          Your account is waiting for admin approval before it can enter the app.
        </p>
        <div className="mb-6 rounded-xl border border-slate-700 bg-slate-950/70 p-4 text-sm text-slate-300">
          <p className="font-medium text-slate-100">
            {authUser.displayName?.trim() || authUser.email?.trim() || 'New Waysprite account'}
          </p>
          <p>{authUser.email?.trim() || 'No email address available on this account.'}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-emerald-700"
            disabled={isRefreshing}
            onClick={() => {
              void handleRefresh();
            }}
            type="button"
          >
            {isRefreshing ? 'Checking...' : 'Check Approval Status'}
          </button>
          <button
            className="rounded-lg border border-slate-500 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-800"
            onClick={() => {
              void signOutUser();
            }}
            type="button"
          >
            Sign Out
          </button>
        </div>
        {statusMessage ? <p className="mt-4 text-sm text-slate-400">{statusMessage}</p> : null}
      </div>
    </div>
  );
}
